import React from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Email, Language } from '@mui/icons-material';
import { BRAND } from '../../constants/branding';
import { darkCardSx, gradientButtonSx } from '../../constants/brandStyles';

export const ContactPage: React.FC = () => {
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: BRAND.colors.text,
      '& fieldset': { borderColor: BRAND.colors.border },
      '&:hover fieldset': { borderColor: 'rgba(0,212,255,0.35)' },
      '&.Mui-focused fieldset': { borderColor: BRAND.colors.primary },
    },
    '& .MuiInputLabel-root': { color: BRAND.colors.textDim },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND.colors.primary },
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
      <Typography variant="overline" sx={{ color: BRAND.colors.primary, fontWeight: 700 }}>
        Contact
      </Typography>
      <Typography variant="h3" fontWeight={800} sx={{ mt: 1, mb: 2 }}>
        We&apos;d love to hear from you
      </Typography>
      <Typography sx={{ mb: 5, color: BRAND.colors.textDim }}>
        Questions about {BRAND.name}, partnerships, or support? Reach out and we&apos;ll get back to you.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Email sx={{ color: BRAND.colors.primary }} />
            <Box>
              <Typography variant="subtitle2" sx={{ color: BRAND.colors.textDim }}>
                Email
              </Typography>
              <Typography component="a" href={`mailto:${BRAND.supportEmail}`} sx={{ color: BRAND.colors.primary }}>
                {BRAND.supportEmail}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Language sx={{ color: BRAND.colors.primary }} />
            <Box>
              <Typography variant="subtitle2" sx={{ color: BRAND.colors.textDim }}>
                Website
              </Typography>
              <Typography component="a" href={BRAND.url} sx={{ color: BRAND.colors.primary }}>
                {BRAND.domain}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box sx={{ ...darkCardSx, p: 3 }}>
            {submitted ? (
              <Alert severity="success" sx={{ bgcolor: 'rgba(0,212,255,0.08)', color: BRAND.colors.text }}>
                Thanks for reaching out! We&apos;ll respond to your message at the email you provided.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Name" name="name" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Email" name="email" type="email" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth required label="Subject" name="subject" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth required multiline rows={5} label="Message" name="message" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" size="large" sx={gradientButtonSx}>
                      Send message
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactPage;
